import React from "react";
import { Dropdown } from "react-bootstrap";
import PlusIcon from "../../../../../icons/plus.icon";
import CrossIcon from "../../../../../icons/cross.icon";
import CheckIcon from "../../../../../icons/check.icon";
import styles from "./tags-dropdown.module.scss";

const TagChip = ({ text, onClose }) => {
  return (
    <div className={styles.tagChip}>
      <div className={styles.tagChipInner}>
        <div className={styles.tagChipLabel}>{text}</div>
      </div>
      {onClose && (
        <button
          type="button"
          className={styles.tagChipClose}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={`Remove ${text} tag`}
        >
          <CrossIcon fill="#008FD4" width={9} height={9} />
        </button>
      )}
    </div>
  );
};

export const TagsDropdown = ({
  menuItems = [],
  inputValue = "",
  onInputChange,
  onInputFocus,
  onItemClick,
  onAddClick,
  selectedItem,
  show = false,
  onToggle,
  selectedTags = [],
  onRemoveTag,
}) => {
  // Filter menu items based on search input
  const filteredMenuItems = React.useMemo(() => {
    if (!inputValue.trim()) {
      return menuItems;
    }
    const searchTerm = inputValue.toLowerCase().trim();
    return menuItems.filter(item => 
      item.label.toLowerCase().includes(searchTerm)
    );
  }, [menuItems, inputValue]);

  // Check if the input value matches any existing menu item
  const inputMatchesExisting = React.useMemo(() => {
    if (!inputValue.trim()) return false;
    const searchTerm = inputValue.toLowerCase().trim();
    return menuItems.some(item => 
      item.label.toLowerCase() === searchTerm
    );
  }, [menuItems, inputValue]);

  const handleItemClick = (label) => {
    if (onItemClick) {
      onItemClick(label);
    }
    if (onToggle) {
      onToggle(false);
    }
  };

  const handleAddClick = () => {
    if (onAddClick) {
      onAddClick();
    }
    if (onToggle) {
      onToggle(false);
    }
  };

  const handleInputFocus = (e) => {
    e.stopPropagation();
    if (onInputFocus) {
      onInputFocus();
    }
    if (onToggle) {
      onToggle(true);
    }
  };

  const handleInputChange = (e) => {
    e.stopPropagation();
    if (onInputChange) {
      onInputChange(e);
    }
    if (onToggle) {
      onToggle(true);
    }
  };

  const handleContainerClick = () => {
    if (onToggle) {
      onToggle(!show);
    }
  };

  return (
    <Dropdown
      show={show}
      onToggle={(isOpen) => {
        if (onToggle) {
          onToggle(isOpen);
        }
      }}
      className={styles.dropdown}
    >
      <Dropdown.Toggle as="div" className={styles.dropdownToggle} onClick={handleContainerClick}>
        <div className={styles.tagsInputWrapper}>
          <div className={styles.tagsInputContainer}>
            <div className={styles.tagsList}>
              {selectedTags.map((tag, index) => (
                <TagChip
                  key={index}
                  text={tag}
                  onClose={() => onRemoveTag && onRemoveTag(tag)}
                />
              ))}
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  className={styles.tagInput}
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onClick={(e) => e.stopPropagation()}
                  placeholder=""
                  aria-label="Tag input"
                />
              </div>
            </div>
          </div>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.dropdownMenu} align="start">
        <div className={styles.menuContent}>
          {filteredMenuItems.length > 0 && (
            <div className={styles.menuItems}>
              {filteredMenuItems.map((item) => {
                const isSelected = selectedTags.includes(item.label);
                return (
                  <button
                    key={item.id}
                    className={`${styles.menuItem} ${
                      isSelected ? styles.menuItemSelected : ""
                    }`}
                    role="menuitem"
                    onClick={() => handleItemClick(item.label)}
                  >
                    <div className={styles.menuItemContent}>
                      <div className={styles.menuItemIconWrapper}>
                        {isSelected ? (
                          <CheckIcon className={styles.menuItemIcon} fill="#616778" width={12} height={12} />
                        ) : (
                          <div className={styles.menuItemIconPlaceholder} />
                        )}
                      </div>
                      <div className={styles.menuItemLabel}>{item.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {inputValue && !inputMatchesExisting && (
            <>
              <div className={styles.menuDivider}>
                <div className={styles.dividerWrapper}>
                  <div style={{ borderTop: "1px solid rgba(235, 237, 241, 1)", width: "100%" }} />
                </div>
              </div>

              <button
                className={styles.addButton}
                role="menuitem"
                onClick={handleAddClick}
                aria-label={`Add new item ${inputValue}`}
              >
                <div className={styles.addButtonContent}>
                  <div className={styles.addIconWrapper}>
                    <PlusIcon 
                      className={styles.addIcon} 
                      fill="#008FD4" 
                      width={16} 
                      height={16}
                      style={{ fill: '#008FD4' }}
                    />
                  </div>
                  <div className={styles.addLabel}>
                    Add &quot;{inputValue}&quot;
                  </div>
                </div>
              </button>
            </>
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

